import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { lc } from '@/i18n/copy';

interface FounderReminderBannerProps {
  daysRemaining: number;
  onDismiss: () => void;
  show: boolean;
}

const copy = {
  en: {
    banner: "You're still eligible for your Founding Member pricing. Reloading keeps your $12 rate locked in.",
    secondary: 'No pressure — just a heads up.',
    reload: 'Reload now',
    dismiss: 'Dismiss',
  },
  es: {
    banner: 'Aún eres elegible para tu precio de Miembro Fundador. Recargar mantiene tu tarifa de $12.',
    secondary: 'Sin presión — solo un aviso.',
    reload: 'Recargar ahora',
    dismiss: 'Descartar',
  },
  fr: {
    banner: 'Vous êtes toujours éligible au tarif Membre Fondateur. Recharger garde votre tarif de 12$ verrouillé.',
    secondary: 'Pas de pression — juste un rappel.',
    reload: 'Recharger maintenant',
    dismiss: 'Ignorer',
  },
  pt: {
    banner: 'Você ainda é elegível ao preço de Membro Fundador. Recarregar mantém sua taxa de $12.',
    secondary: 'Sem pressão — apenas um aviso.',
    reload: 'Recarregar agora',
    dismiss: 'Dispensar',
  },
  ar: {
    banner: 'لا تزال مؤهلاً لسعر العضو المؤسس. إعادة الشحن تحافظ على سعرك البالغ 12$.',
    secondary: 'بدون ضغط — مجرد تنبيه.',
    reload: 'إعادة شحن الآن',
    dismiss: 'تجاهل',
  },
  ja: {
    banner: '創設メンバー価格の資格がまだあります。リロードで$12レートが維持されます。',
    secondary: 'プレッシャーなし — ただのお知らせです。',
    reload: '今すぐリロード',
    dismiss: '閉じる',
  },
  ko: {
    banner: '아직 창립 멤버 가격 자격이 있습니다. 리로드하면 $12 요금이 유지됩니다.',
    secondary: '부담 없이 — 그냥 알려드리는 거예요.',
    reload: '지금 리로드',
    dismiss: '닫기',
  },
  id: {
    banner: 'Anda masih memenuhi syarat harga Anggota Pendiri. Mengisi ulang menjaga tarif $12 Anda.',
    secondary: 'Tanpa tekanan — sekadar informasi.',
    reload: 'Isi ulang sekarang',
    dismiss: 'Tutup',
  },

  hi: {
    banner: "You're still eligible for your Founding Member pricing. Reloading keeps your $12 rate locked in.",
    secondary: 'कोई दबाव नहीं — बस एक सूचना।',
    reload: 'अभी रीलोड करें',
    dismiss: 'खारिज करें',
  },
};

export function FounderReminderBanner({ daysRemaining, onDismiss, show }: FounderReminderBannerProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = lc(copy, language);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-6"
        >
          <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{t.banner}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.secondary}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={onDismiss}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2 mt-3 ml-8">
              <Button size="sm" onClick={() => navigate('/add-credits')}>
                {t.reload}
              </Button>
              <Button size="sm" variant="ghost" onClick={onDismiss}>
                {t.dismiss}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
